import type {
  LayoutAlgorithm,
  LayoutDirection,
  LayoutEdge,
  LayoutEngine,
  LayoutNode,
  LayoutRequest,
  LayoutResult,
  LayoutResultNode,
} from "./index"

export const NODE_WIDTH = 240
export const NODE_HEIGHT = 120

const ALGO_MAP: Record<LayoutAlgorithm, number> = {
  layered: 0,
  mrtree: 1,
  stress: 2,
  force: 3,
  radial: 4,
}

const EDGE_KIND_MAP: Record<string, number> = {
  hierarchy: 0,
  dependency: 1,
  association: 2,
}

interface WorkerMessageEvent {
  data: unknown
}

interface WorkerErrorEvent {
  message?: string
  error?: unknown
  preventDefault?(): void
}

interface WorkerPort {
  onmessage: ((event: WorkerMessageEvent) => void) | null
  onerror: ((event: WorkerErrorEvent) => void) | null
  onmessageerror: ((event: WorkerErrorEvent) => void) | null
  postMessage(message: unknown, transfer: Transferable[]): void
  terminate(): void
}

type WorkerFactory = () => WorkerPort

interface WorkerResponse {
  id: number
  outputBuffer?: ArrayBuffer
  error?: string
}

interface PendingRequest {
  nodes: readonly LayoutNode[]
  resolve: (result: LayoutResult) => void
  reject: (reason: unknown) => void
  cleanupAbort: () => void
}

class WorkerLayoutEngine implements LayoutEngine {
  private worker: WorkerPort | null = null
  private nextId = 0
  private readonly pending = new Map<number, PendingRequest>()
  private disposed = false

  constructor(private readonly workerFactory: WorkerFactory) {}

  async layout(request: LayoutRequest): Promise<LayoutResult> {
    if (this.disposed) throw new Error("Layout engine disposed")
    if (request.signal?.aborted) throw abortReason(request.signal)
    if (request.nodes.length === 0) return { nodes: [] }

    const nodes = [...request.nodes]
    const inputBuffer = encodeLayoutInput(
      nodes,
      [...request.edges],
      request.direction ?? "DOWN",
      request.algorithm ?? "layered",
    )

    let worker: WorkerPort
    try {
      worker = this.getWorker()
    } catch (error) {
      throw toError(error, "Failed to start layout worker")
    }

    const id = this.nextId++
    return new Promise<LayoutResult>((resolve, reject) => {
      const onAbort = () => {
        const pending = this.takePending(id)
        pending?.reject(abortReason(request.signal!))
      }
      const cleanupAbort = () =>
        request.signal?.removeEventListener("abort", onAbort)
      this.pending.set(id, { nodes, resolve, reject, cleanupAbort })
      request.signal?.addEventListener("abort", onAbort, { once: true })

      if (request.signal?.aborted) {
        onAbort()
        return
      }

      try {
        worker.postMessage({ id, inputBuffer }, [inputBuffer])
      } catch (error) {
        const pending = this.takePending(id)
        pending?.reject(toError(error, "Failed to post layout request"))
      }
    })
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    this.worker?.terminate()
    this.worker = null
    this.rejectAll(new Error("Layout engine disposed"))
  }

  private getWorker() {
    if (this.worker) return this.worker
    const worker = this.workerFactory()
    worker.onmessage = (event) => this.handleMessage(event)
    worker.onerror = (event) => this.handleWorkerError(event)
    worker.onmessageerror = (event) => this.handleWorkerError(event)
    this.worker = worker
    return worker
  }

  private handleMessage(event: WorkerMessageEvent) {
    const response = event.data as Partial<WorkerResponse> | null
    if (!response || typeof response.id !== "number") return
    const pending = this.takePending(response.id)
    if (!pending) return
    if (response.error) {
      pending.reject(new Error(response.error))
      return
    }
    if (!(response.outputBuffer instanceof ArrayBuffer)) {
      pending.reject(new Error("Layout worker returned an invalid response"))
      return
    }
    try {
      pending.resolve({
        nodes: decodeLayoutOutput(response.outputBuffer, pending.nodes),
      })
    } catch (error) {
      pending.reject(toError(error, "Invalid layout worker output"))
    }
  }

  private handleWorkerError(event: WorkerErrorEvent) {
    event.preventDefault?.()
    const error = toError(event.error ?? event.message, "Layout worker failed")
    this.worker?.terminate()
    this.worker = null
    this.rejectAll(error)
  }

  private takePending(id: number) {
    const pending = this.pending.get(id)
    if (!pending) return undefined
    this.pending.delete(id)
    pending.cleanupAbort()
    return pending
  }

  private rejectAll(error: Error) {
    for (const id of [...this.pending.keys()]) {
      this.takePending(id)?.reject(error)
    }
  }
}

export function createWorkerLayoutEngine(
  workerFactory: WorkerFactory = () =>
    new Worker(new URL("/viewer/layout/worker.js", location.href), {
      type: "module",
    }) as unknown as WorkerPort,
): LayoutEngine {
  return new WorkerLayoutEngine(workerFactory)
}

function encodeLayoutInput(
  nodes: readonly LayoutNode[],
  edges: readonly LayoutEdge[],
  direction: LayoutDirection,
  algorithm: LayoutAlgorithm,
) {
  const idToIndex = new Map<string, number>()
  for (let i = 0; i < nodes.length; i++) idToIndex.set(nodes[i].id, i)

  const kindToId = new Map<string, number>()
  let nextKindId = 0
  for (const node of nodes) {
    const kind = node.kind ?? ""
    if (!kindToId.has(kind)) kindToId.set(kind, nextKindId++)
  }

  const headerSize = 16
  const nodeStride = 12
  const edgeStride = 12
  const buffer = new ArrayBuffer(
    headerSize + nodes.length * nodeStride + edges.length * edgeStride,
  )
  const view = new DataView(buffer)
  view.setUint32(0, nodes.length, true)
  view.setUint32(4, edges.length, true)
  view.setUint32(8, ALGO_MAP[algorithm], true)
  view.setUint32(12, direction === "DOWN" ? 0 : 1, true)

  let offset = headerSize
  for (const node of nodes) {
    view.setFloat32(offset, node.width ?? NODE_WIDTH, true)
    view.setFloat32(offset + 4, node.height ?? NODE_HEIGHT, true)
    view.setUint32(offset + 8, kindToId.get(node.kind ?? "") ?? 0, true)
    offset += nodeStride
  }

  for (const edge of edges) {
    view.setUint32(offset, idToIndex.get(edge.source) ?? 0, true)
    view.setUint32(offset + 4, idToIndex.get(edge.target) ?? 0, true)
    view.setUint32(
      offset + 8,
      EDGE_KIND_MAP[edge.edgeKind ?? "association"] ?? 2,
      true,
    )
    offset += edgeStride
  }
  return buffer
}

function decodeLayoutOutput(
  outputBuffer: ArrayBuffer,
  nodes: readonly LayoutNode[],
): LayoutResultNode[] {
  const expectedBytes = nodes.length * 16
  if (outputBuffer.byteLength < expectedBytes) {
    throw new Error(
      `Layout worker returned ${outputBuffer.byteLength} bytes; expected ${expectedBytes}`,
    )
  }
  const out = new DataView(outputBuffer)
  return nodes.map((node, index) => {
    const offset = index * 16
    return {
      id: node.id,
      x: out.getFloat32(offset, true),
      y: out.getFloat32(offset + 4, true),
      width: out.getFloat32(offset + 8, true),
      height: out.getFloat32(offset + 12, true),
    }
  })
}

function abortReason(signal: AbortSignal) {
  if (signal.reason !== undefined) return signal.reason
  return new DOMException("Layout request aborted", "AbortError")
}

function toError(value: unknown, fallback: string) {
  if (value instanceof Error) return value
  if (typeof value === "string" && value) return new Error(value)
  return new Error(fallback)
}
